import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { BrandWordmark } from "@/components/Brand";
import SectionBadge from "@/components/SectionBadge";
import WaveBackground from "@/components/WaveBackground";

const segmentOptions = [
  "Financeiro / Bancário",
  "Seguros",
  "Telecom",
  "Saúde",
  "Varejo / E-commerce",
  "Energia / Utilities",
  "Indústria",
  "Educação",
  "Governo / Público",
  "Outro",
];

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const formatPhone = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const isValidBRPhone = (v) => {
  const d = v.replace(/\D/g, "");
  return d.length === 10 || d.length === 11;
};

const RequestDemoPage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    segment: "",
    phone: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Informe seu nome completo.";
    if (!form.email.trim() || !isEmail(form.email)) e.email = "Informe um e-mail válido.";
    if (!form.company.trim()) e.company = "Informe sua empresa.";
    if (!form.segment) e.segment = "Selecione um segmento.";
    if (!form.phone.trim() || !isValidBRPhone(form.phone)) e.phone = "Informe um telefone válido com DDD.";
    if (!form.consent) e.consent = "É necessário aceitar a Política e os Termos.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    // Simulate submission delay; no backend wired per requirements.
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 700);
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white relative overflow-hidden">
      <WaveBackground opacity={0.4} />
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] glow-radial opacity-60 pointer-events-none"
      />

      {/* Compact top bar */}
      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[80px] flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="form-back-logo">
            <BrandWordmark className="h-14 sm:h-16 w-auto" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] text-[#b8c0cc] hover:text-white transition-colors"
            data-testid="form-back-link"
          >
            <ArrowLeft size={14} />
            Voltar ao site
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <div className="text-center mb-10">
          <SectionBadge>Solicitar Demonstração</SectionBadge>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-tight text-white text-balance">
            Conheça a plataforma <span className="text-[#1dc7e9]">Documento Express</span>
          </h1>
          <p className="mt-4 text-[#b8c0cc] leading-relaxed max-w-xl mx-auto">
            Preencha o formulário e nosso time entrará em contato para apresentar
            como podemos transformar a comunicação digital corporativa da sua
            empresa.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative rounded-3xl border border-[#1dc7e9]/25 bg-[#0b1220]/70 p-6 sm:p-9 backdrop-blur-md"
          style={{
            boxShadow:
              "0 0 0 1px rgba(29,199,233,0.08), 0 30px 80px rgba(0,0,0,0.5)",
          }}
        >
          {submitted ? (
            <div className="text-center py-8" data-testid="form-success">
              <div className="w-16 h-16 rounded-full bg-[#1dc7e9]/15 border border-[#1dc7e9]/40 flex items-center justify-center mx-auto mb-5 text-[#1dc7e9]">
                <CheckCircle2 size={28} strokeWidth={2} />
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight mb-2">
                Solicitação enviada com sucesso
              </h2>
              <p className="text-[#b8c0cc] leading-relaxed max-w-md mx-auto">
                Em breve, um especialista entrará em contato.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-7 bg-[#1dc7e9] text-[#04060d] font-bold text-sm px-6 py-3.5 rounded-lg hover:shadow-[0_0_30px_rgba(29,199,233,0.5)] transition-all duration-300"
                data-testid="form-success-back"
              >
                Voltar ao site
                <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="demo-form" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  id="name"
                  label="Nome"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  error={errors.name}
                  placeholder="Seu nome completo"
                  autoComplete="name"
                />
                <Field
                  id="email"
                  label="E-mail"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  error={errors.email}
                  placeholder="voce@empresa.com.br"
                  autoComplete="email"
                />
                <Field
                  id="company"
                  label="Empresa"
                  value={form.company}
                  onChange={(v) => update("company", v)}
                  error={errors.company}
                  placeholder="Nome da empresa"
                  autoComplete="organization"
                />
                <SelectField
                  id="segment"
                  label="Segmento"
                  value={form.segment}
                  onChange={(v) => update("segment", v)}
                  options={segmentOptions}
                  error={errors.segment}
                />
                <div className="sm:col-span-2">
                  <Field
                    id="phone"
                    label="Telefone"
                    type="tel"
                    value={form.phone}
                    onChange={(v) => update("phone", formatPhone(v))}
                    error={errors.phone}
                    placeholder="(11) 99999-9999"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <label
                htmlFor="consent"
                className={`flex items-start gap-3 mt-2 cursor-pointer select-none ${
                  errors.consent ? "text-rose-300" : "text-[#b8c0cc]"
                }`}
              >
                <span className="relative mt-0.5 shrink-0">
                  <input
                    id="consent"
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="peer absolute opacity-0 w-5 h-5 cursor-pointer"
                    data-testid="form-consent"
                  />
                  <span
                    className={`block w-5 h-5 rounded border ${
                      form.consent
                        ? "bg-[#1dc7e9] border-[#1dc7e9]"
                        : "bg-transparent border-[#1dc7e9]/40"
                    } transition-all flex items-center justify-center`}
                  >
                    {form.consent && (
                      <CheckCircle2 size={12} className="text-[#04060d]" strokeWidth={3} />
                    )}
                  </span>
                </span>
                <span className="text-sm leading-relaxed">
                  Li e concordo com a{" "}
                  <Link
                    to="/politica-de-privacidade"
                    className="text-[#1dc7e9] underline-offset-2 hover:underline"
                  >
                    Política de Privacidade
                  </Link>{" "}
                  e os{" "}
                  <Link
                    to="/termos-de-uso"
                    className="text-[#1dc7e9] underline-offset-2 hover:underline"
                  >
                    Termos de Uso
                  </Link>
                  .
                </span>
              </label>
              {errors.consent && (
                <p className="text-xs text-rose-400 -mt-3">{errors.consent}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 bg-[#1dc7e9] text-[#04060d] font-bold text-sm px-6 py-4 rounded-lg hover:shadow-[0_0_36px_rgba(29,199,233,0.5)] transition-all duration-300 disabled:opacity-70"
                data-testid="form-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Solicitação
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
};

const Field = ({ id, label, value, onChange, error, type = "text", placeholder, autoComplete }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold tracking-wide text-[#b8c0cc] mb-1.5">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-testid={`form-${id}`}
      className={`w-full bg-[#050816]/60 text-white placeholder-[#b8c0cc]/40 border ${
        error ? "border-rose-400/60" : "border-[#1dc7e9]/20"
      } rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#1dc7e9]/70 focus:ring-1 focus:ring-[#1dc7e9]/30 transition-all`}
    />
    {error && <p className="text-xs text-rose-400 mt-1">{error}</p>}
  </div>
);

const SelectField = ({ id, label, value, onChange, options, error }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold tracking-wide text-[#b8c0cc] mb-1.5">
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`form-${id}`}
      className={`w-full bg-[#050816]/60 text-white border ${
        error ? "border-rose-400/60" : "border-[#1dc7e9]/20"
      } rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#1dc7e9]/70 focus:ring-1 focus:ring-[#1dc7e9]/30 transition-all`}
    >
      <option value="" className="bg-[#0b1220]">Selecione um segmento</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0b1220]">
          {o}
        </option>
      ))}
    </select>
    {error && <p className="text-xs text-rose-400 mt-1">{error}</p>}
  </div>
);

export default RequestDemoPage;
